import { FormEvent, useEffect, useState } from 'react';
import {
  company,
  contactSection,
  Flow,
  flowMeta,
  flowOrder,
} from '../content/site';
import { FlowKey } from '../hooks/useFlow';
import { Arrow, Check } from './Icons';
import { Reveal } from './Reveal';
import './Contact.css';

type Props = {
  flowKey: FlowKey;
  onChangeFlow: (flow: Flow | null) => void;
};

type Status = 'idle' | 'sending' | 'sent' | 'error';

/**
 * Formspree endpoint for enquiries (`https://formspree.io/f/<form-id>`), baked
 * in at build time. With nothing configured the form falls back to opening a
 * pre-filled email, so the site is never a dead end — but that fallback needs
 * the visitor to press send themselves. Configure this for real delivery.
 *
 * Changing the host also means updating `connect-src` in
 * docker/security-headers.conf, or the CSP will block the request.
 *
 * Read on submit rather than at module load — webpack inlines the value either
 * way, and it keeps the endpoint swappable in tests.
 */
function enquiryEndpoint() {
  return process.env.REACT_APP_ENQUIRY_ENDPOINT;
}

const intentOptions: { value: FlowKey; label: string }[] = [
  ...flowOrder.map((flow) => ({
    value: flow as FlowKey,
    label: flowMeta[flow].cta,
  })),
  { value: 'undecided', label: "I'm not sure yet — I'd like to talk it through" },
];

/** Human labels for the mailto fallback, so the draft reads as a real email. */
const fieldLabels: [string, string][] = [
  ['intent', 'Considering'],
  ['name', 'Name'],
  ['firm', 'Firm'],
  ['email', 'Email'],
  ['phone', 'Phone'],
  ['willCount', 'Wills held (approx.)'],
  ['message', 'Notes'],
];

function composeEmailBody(payload: Record<string, FormDataEntryValue>) {
  const intentLabel =
    intentOptions.find((option) => option.value === payload.intent)?.label ??
    String(payload.intent ?? '');

  return fieldLabels
    .map(([key, label]) => {
      const value = key === 'intent' ? intentLabel : String(payload[key] ?? '');
      return value.trim() ? `${label}: ${value}` : null;
    })
    .filter(Boolean)
    .join('\n');
}

export function Contact({ flowKey, onChangeFlow }: Props) {
  const [intent, setIntent] = useState<FlowKey>(flowKey);
  const [status, setStatus] = useState<Status>('idle');

  // Keep the form in step with the buttons elsewhere on the page.
  useEffect(() => setIntent(flowKey), [flowKey]);

  const handleIntent = (value: FlowKey) => {
    setIntent(value);
    onChangeFlow(value === 'undecided' ? null : value);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    // Honeypot: bots fill hidden fields, people do not. Show them success so
    // they have nothing to learn from, then stop.
    if (data.get('_gotcha')) {
      setStatus('sent');
      return;
    }
    data.delete('_gotcha');

    const payload = Object.fromEntries(data.entries());
    const subject = `Confidential enquiry — ${payload.firm || 'new enquiry'}`;
    const endpoint = enquiryEndpoint();
    setStatus('sending');

    if (!endpoint) {
      window.location.href = `mailto:${company.email}?subject=${encodeURIComponent(
        subject,
      )}&body=${encodeURIComponent(composeEmailBody(payload))}`;
      setStatus('sent');
      return;
    }

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Formspree redirects to its own thank-you page without this.
          Accept: 'application/json',
        },
        // `_subject` sets the subject line of the email Formspree sends on.
        body: JSON.stringify({ ...payload, _subject: subject }),
      });

      if (!response.ok) {
        const detail: { errors?: { message?: string }[] } | null = await response
          .json()
          .catch(() => null);
        const reason = detail?.errors
          ?.map((error) => error.message)
          .filter(Boolean)
          .join('; ');
        throw new Error(reason || `Request failed: ${response.status}`);
      }

      setStatus('sent');
      form.reset();
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="section section--ink" id="enquire">
      <div className="container contact">
        <Reveal className="contact__intro-reveal">
          <div className="contact__intro">
            <p className="eyebrow">{contactSection.eyebrow}</p>
            <h2 className="section-title">{contactSection.headings[flowKey]}</h2>
            <p className="section-lead">{contactSection.lead}</p>

            <ul className="contact__assurances">
              {contactSection.assurances.map((item) => (
                <li key={item} className="contact__assurance">
                  <Check className="contact__check" />
                  {item}
                </li>
              ))}
            </ul>

            <div className="contact__direct">
              <p className="contact__direct-label">Or email us directly</p>
              <a className="contact__direct-link" href={`mailto:${company.email}`}>
                {company.email}
              </a>
            </div>
          </div>
        </Reveal>

        <Reveal className="contact__form-reveal">
          {status === 'sent' ? (
            <div className="contact__card contact__success" role="status">
              <span className="contact__success-icon">
                <Check width={26} height={26} />
              </span>
              <h3 className="contact__success-title">Thank you — that's with us</h3>
              <p className="contact__success-body">
                A principal will reply within one working day, from a named
                address rather than a no-reply inbox. If it is urgent, email{' '}
                <a href={`mailto:${company.email}`}>{company.email}</a> and say
                so.
              </p>
            </div>
          ) : (
            <form className="contact__card" onSubmit={handleSubmit} noValidate={false}>
              <div className="field field--full">
                <label className="field__label" htmlFor="intent">
                  What are you considering?
                </label>
                <select
                  id="intent"
                  name="intent"
                  className="field__input"
                  value={intent}
                  onChange={(event) => handleIntent(event.target.value as FlowKey)}
                >
                  {intentOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label className="field__label" htmlFor="name">
                  Your name
                </label>
                <input
                  id="name"
                  name="name"
                  className="field__input"
                  type="text"
                  autoComplete="name"
                  required
                />
              </div>

              <div className="field">
                <label className="field__label" htmlFor="firm">
                  Firm name
                </label>
                <input
                  id="firm"
                  name="firm"
                  className="field__input"
                  type="text"
                  autoComplete="organization"
                  required
                />
              </div>

              <div className="field">
                <label className="field__label" htmlFor="email">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  className="field__input"
                  type="email"
                  autoComplete="email"
                  required
                />
              </div>

              <div className="field">
                <label className="field__label" htmlFor="phone">
                  Phone
                </label>
                <input
                  id="phone"
                  name="phone"
                  className="field__input"
                  type="tel"
                  autoComplete="tel"
                />
              </div>

              {intent !== 'practice' && (
                <div className="field field--full">
                  <label className="field__label" htmlFor="willCount">
                    Approximate number of wills held{' '}
                    <span className="field__optional">optional</span>
                  </label>
                  <input
                    id="willCount"
                    name="willCount"
                    className="field__input"
                    type="text"
                    inputMode="numeric"
                    placeholder="A rough figure is fine"
                  />
                </div>
              )}

              <div className="field field--full">
                <label className="field__label" htmlFor="message">
                  Anything you would like us to know{' '}
                  <span className="field__optional">optional</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  className="field__input field__input--area"
                  rows={4}
                  placeholder="Timescales, what you are hoping for, or the question you actually want answered."
                />
              </div>

              {/* Honeypot — visually hidden, never shown to people. Named
                  `_gotcha` so Formspree's own spam filter catches it too. */}
              <div className="visually-hidden" aria-hidden>
                <label htmlFor="_gotcha">Website</label>
                <input id="_gotcha" name="_gotcha" type="text" tabIndex={-1} />
              </div>

              <label className="consent field--full">
                <input type="checkbox" name="consent" required />
                <span>
                  I am happy for {company.name} to contact me about this enquiry.
                  No client or financial detail is shared before a mutual NDA.
                </span>
              </label>

              <div className="field--full">
                <button
                  type="submit"
                  className="btn btn--brass btn--block"
                  disabled={status === 'sending'}
                >
                  {status === 'sending' ? 'Sending…' : 'Send confidentially'}
                  {status !== 'sending' && <Arrow className="btn__arrow" />}
                </button>

                {status === 'error' && (
                  <p className="contact__error" role="alert">
                    Something went wrong sending that. Please email{' '}
                    <a href={`mailto:${company.email}`}>{company.email}</a>{' '}
                    directly and we will pick it up from there.
                  </p>
                )}
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
